import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useWebSocket } from './useWebSocket';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { readStorage, writeStorage } from '../safeStorage';

// ── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_STOCK = {
  symbol:           '',
  incrementPrice:   0.1,
  buySellRange:     0.1,
  orderQuantity:    1,
  stepsAbove:       2,
  stepsBelow:       2,
  minBuy:           0,
  maxSell:          0,
  keepAbove:        1,
  trailingBuyStop:  0,
  trailingSellStop: 0,
  offset:           0,
};

const EDITABLE_FIELDS = [
  { label: 'Steps Above',        field: 'stepsAbove',       step: '1',    parse: parseInt },
  { label: 'Steps Below',        field: 'stepsBelow',       step: '1',    parse: parseInt },
  { label: 'Min Buy',            field: 'minBuy',           step: '0.01', parse: parseFloat },
  { label: 'Min Sell',           field: 'maxSell',          step: '0.01', parse: parseFloat },
  { label: 'Keep Above',         field: 'keepAbove',        step: '1',    parse: parseInt },
  { label: 'Trailing Buy Stop',  field: 'trailingBuyStop',  step: '0.01', parse: parseFloat },
  { label: 'Trailing Sell Stop', field: 'trailingSellStop', step: '0.01', parse: parseFloat },
];

/**
 * State shape reference:
 *
 * accounts: {
 *   158392091: {
 *     accountName: "My Account",
 *     stocks: {
 *       "AAPL": {
 *         incrementPrice, buySellRange, orderQuantity,
 *         stepsAbove, stepsBelow, minBuy, maxSell,
 *         keepAbove, trailingBuyStop, trailingSellStop, enabled
 *       }
 *     },
 *     orders: { '<orderId>': { "AAPL": { buy: [...], sell: [...] } } },
 *     logs: [...]
 *   }
 * }
 *
 * askBid: { "AAPL": { ask: 150.00, bid: 149.00 } }
 */

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Formats a quote to a fixed 2 decimals.
 *
 * JSON numbers drop trailing zeros, so a price arrives as 7.2 one tick and 7.25
 * the next — rendered raw that is a one-character width change on every update,
 * which in a wrapping row is enough to shove the neighbouring value onto its own
 * line. Pair with .quote-value, whose min-width absorbs a digit-count change
 * (9.95 → 10.05) too.
 */
const fmtPrice = (v) => (v == null ? 'N/A' : Number(v).toFixed(2));

const flashClass = (dir) => (dir === 'up' ? 'flash-up' : dir === 'down' ? 'flash-down' : '');

// ── Component ────────────────────────────────────────────────────────────────

export default function TradingDashboard() {
  // ── State ──────────────────────────────────────────────────────────────────
  const [appState, setAppState]         = useState({ accounts: {}, askBid: {} });
  const [orders,   setOrders]           = useState({});
  const [currentId, setCurrentId]       = useState(null);
  const [symbol, setSymbol]             = useState('');
  const [fillCooldown, setFillCooldown] = useState(false);
  const [maintainCooldown, setMaintainCooldown] = useState(false);
  const [reconnectCooldown, setReconnectCooldown] = useState(false);
  const [detailsOpen, setDetailsOpen]   = useState(false);
  const [detailEdits, setDetailEdits]   = useState({});
  const [newStock, setNewStock]         = useState(DEFAULT_STOCK);
  const [bidFlash, setBidFlash]         = useState(null); // 'up' | 'down' | null
  const [askFlash, setAskFlash]         = useState(null);
  const [dark, setDark]                 = useState(() => readStorage('sono-dark-mode') === 'true');

  const styles = makeStyles(dark);

  const prevBidRef = useRef(null);
  const prevAskRef = useRef(null);

  const { supported: pushSupported, subscribed: pushSubscribed, loading: pushLoading,
          error: pushError, subscribe: pushSubscribe, unsubscribe: pushUnsubscribe }
    = usePushNotifications();

  const setField = (field, value) => setNewStock(s => ({ ...s, [field]: value }));

  // ── Effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    document.body.style.background = dark ? '#121212' : '#fff';
    writeStorage('sono-dark-mode', dark);
  }, [dark]);

  useEffect(() => {
    axios.get('/api/state')
      .then(res => {
        setAppState(res.data);
        setOrders(res.data.orders ?? {});
      })
      .catch(err => console.error('Failed to load initial state', err));
  }, []);

  useEffect(() => {
    setDetailEdits({});
    prevBidRef.current = null;
    prevAskRef.current = null;
    setBidFlash(null);
    setAskFlash(null);
  }, [symbol]);

  const currentBid = appState.askBid?.[symbol]?.bid ?? null;
  const currentAsk = appState.askBid?.[symbol]?.ask ?? null;

  useEffect(() => {
    if (currentBid == null || prevBidRef.current == null) { prevBidRef.current = currentBid; return; }
    if (currentBid !== prevBidRef.current) {
      setBidFlash(currentBid > prevBidRef.current ? 'up' : 'down');
      prevBidRef.current = currentBid;
      const t = setTimeout(() => setBidFlash(null), 700);
      return () => clearTimeout(t);
    }
  }, [currentBid]);

  useEffect(() => {
    if (currentAsk == null || prevAskRef.current == null) { prevAskRef.current = currentAsk; return; }
    if (currentAsk !== prevAskRef.current) {
      setAskFlash(currentAsk > prevAskRef.current ? 'up' : 'down');
      prevAskRef.current = currentAsk;
      const t = setTimeout(() => setAskFlash(null), 700);
      return () => clearTimeout(t);
    }
  }, [currentAsk]);

  // ── WebSocket ──────────────────────────────────────────────────────────────
  const wsUrl = `${window.location.protocol}//${window.location.host}/ws`;
  const { connected, send } = useWebSocket(
    wsUrl,
    ['/topic/state', '/topic/orders'],
    (topic, body) => {
      if (topic === '/topic/orders') {
        setOrders(body);
      } else {
        setAppState(body);
        if (body.orders) setOrders(body.orders);
      }
    }
  );

  // Ask for a snapshot as soon as the socket is up. The server pushes state only
  // when something changes and retains nothing, so on a fresh page load there is
  // nothing to receive until the next fill or scheduled sweep — the dashboard sat
  // empty for as long as that took while trading carried on.
  //
  // Runs after onConnect has registered the subscriptions, so the reply cannot
  // arrive before there is anything listening for it.
  useEffect(() => {
    if (connected) send('/app/getState', {});
  }, [connected, send]);

  // Keep the selection pointed at an account that still exists.
  //
  // currentId is only ever assigned by clicking a button, and the account list is
  // a mirror of what Schwab reports — re-linking with a different account drops the
  // old one from state. The selection then referenced an account that was gone, and
  // every lookup keyed on it returned undefined, so the stock list and both order
  // tables rendered empty with nothing on screen to say the account itself had
  // disappeared. Falling back to the first available account also means a fresh
  // page load shows something instead of waiting for a click.
  useEffect(() => {
    const ids = Object.keys(appState.accounts ?? {});
    if (!ids.length) return;
    if (currentId === null || !ids.includes(String(currentId))) {
      setCurrentId(Number(ids[0]));
      setSymbol('');
    }
  }, [appState.accounts, currentId]);

  // ── Commands ───────────────────────────────────────────────────────────────
  const commands = {
    setEnabled:        (account, symbol, enabled)      => send('/app/setEnabled',        { account, symbol, enabled }),
    fillMissing:       (account, symbol)               => send('/app/fillMissing',       { account, symbol }),
    checkAndMaintain:  (account, symbol)               => send('/app/checkAndMaintain',  { account, symbol }),
    addStock:         (account, stock)                => send('/app/addStock',         { account, ...stock }),
    removeStock:      (account, symbol)               => send('/app/removeStock',      { account, symbol }),
    cancelAllOrders:  (account, symbol)               => send('/app/cancelAllOrders',  { account, symbol }),
    updateStockField: (account, symbol, field, value) => send('/app/updateStockField', { account, symbol, field, value }),
    reconnectFeed:    ()                               => send('/app/reconnectFeed',    {}),
  };

  const handleReconnectFeed = () => {
    commands.reconnectFeed();
    setReconnectCooldown(true);
    setTimeout(() => setReconnectCooldown(false), 10000);
  };

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleAdd = (e) => {
    e.preventDefault();
    if (!newStock.symbol.trim()) return;
    commands.addStock(currentId, newStock);
    setNewStock(DEFAULT_STOCK);
    setSymbol('');
  };
  const handleDelete = () => {
    if (!window.confirm(`Are you sure you want to delete ${symbol}?`)) return;
    commands.removeStock(currentId, symbol);
    setSymbol('');
  };
  const handleCopyDelete = (currentId, symbol, stock) => {
    if (!window.confirm(`Are you sure you want to copy and delete ${symbol}?`)) return;
    const copyStock = {
      symbol:           symbol,
      incrementPrice:   stock?.incrementPrice ?? 0.1,
      buySellRange:     stock?.buySellRange ?? 0.1,
      orderQuantity:    stock?.orderQuantity ?? 1,
      stepsAbove:       stock?.stepsAbove ?? 2,
      stepsBelow:       stock?.stepsBelow ?? 2,
      minBuy:           stock?.minBuy ?? 0,
      maxSell:          stock?.maxSell ?? 0,
      keepAbove:        stock?.keepAbove ?? 1,
      trailingBuyStop:  stock?.trailingBuyStop ?? 0,
      trailingSellStop: stock?.trailingSellStop ?? 0,
      offset:           stock?.offset ?? 0,
    };
    commands.removeStock(currentId, symbol);
    setSymbol('');
    setNewStock(copyStock);
  }

  // ── Render helpers ─────────────────────────────────────────────────────────
  const renderDetailsBody = () => {
    const s = appState.accounts?.[currentId]?.stocks?.[symbol] ?? {};
    return (
      <div style={styles.accordionBody}>
        <table style={styles.detailTable}>
          <tbody>
            {[
              ['Enabled',         String(s.enabled ?? '—')],
              ['Increment Price', s.incrementPrice ?? '—'],
              ['Buy/Sell Range',  s.buySellRange ?? '—'],
              ['Order Quantity',  s.orderQuantity ?? '—'],
              ['Offset',          s.offset ?? '—'],
            ].map(([label, val]) => (
              <tr key={label}>
                <td style={styles.detailLabel}>{label}</td>
                <td style={styles.detailValue} colSpan={2}>{val}</td>
              </tr>
            ))}
            {EDITABLE_FIELDS.map(({ label, field, step, parse }) => (
              <tr key={field}>
                <td style={styles.detailLabel}>{label}</td>
                <td style={styles.detailValue}>
                  <input
                    style={{ ...styles.input, width: 80, padding: '4px 6px', fontSize: 13 }}
                    type="number"
                    step={step}
                    value={detailEdits[field] ?? String(s[field] ?? '')}
                    onChange={e => setDetailEdits(d => ({ ...d, [field]: e.target.value }))}
                  />
                </td>
                <td style={{ paddingLeft: 8 }}>
                  <button
                    style={styles.btn('#1976d2', true)}
                    disabled={!connected}
                    onClick={() => commands.updateStockField(currentId, symbol, field, parse(detailEdits[field] ?? s[field]))}
                  >
                    Save
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // ── Derived ────────────────────────────────────────────────────────────────
  const stock = appState.accounts?.[currentId]?.stocks?.[symbol];

  // ── JSX ────────────────────────────────────────────────────────────────────
  return (
    <div style={styles.page}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .sono-spinner {
          display: inline-block; width: 12px; height: 12px;
          border: 2px solid rgba(255,255,255,0.4);
          border-top-color: #fff; border-radius: 50%;
          animation: spin 0.7s linear infinite;
          margin-right: 6px; vertical-align: middle;
        }
        @keyframes flashGreen {
          0%   { background: #4caf50; color: #fff; }
          100% { background: transparent; color: inherit; }
        }
        @keyframes flashRed {
          0%   { background: #f44336; color: #fff; }
          100% { background: transparent; color: inherit; }
        }
        /* Padding and radius live on the base class so they are ALWAYS applied.
           They used to sit on the flash classes, which meant every tick added
           8px of horizontal padding for 700ms and then removed it — enough to
           push the next item onto a new line in a wrapping row. The flash now
           changes colour only and cannot affect layout. */
        .quote      { border-radius: 3px; padding: 1px 4px; white-space: nowrap; }
        .flash-up   { animation: flashGreen 0.7s ease-out; }
        .flash-down { animation: flashRed   0.7s ease-out; }
        /* Fixed-width digits: in a proportional font "1" is narrower than "8",
           so even a same-length price changes width as it ticks. */
        .quote-value { font-variant-numeric: tabular-nums; display: inline-block;
                       min-width: 6ch; text-align: right; }
      `}</style>

      {/* Status bar */}
      <div style={styles.statusBar}>
        <span>
          WebSocket
          <span style={{ ...styles.dot, background: connected ? '#4caf50' : '#f44336' }} />
          {connected ? 'connected' : 'disconnected'}
        </span>
        <span style={{ marginLeft: 16 }}>
          Schwab
          <span style={{ ...styles.dot, background: appState.schwabConnected ? '#4caf50' : '#f44336' }} />
          {appState.schwabConnected ? 'connected' : 'disconnected'}
        </span>
        <button
          style={styles.btn('#ff9800', true)}
          disabled={!connected || reconnectCooldown}
          onClick={handleReconnectFeed}
          title="Reconnect the data feed if ask/bid stops streaming"
        >
          {reconnectCooldown ? <><span className="sono-spinner" />Reconnecting…</> : 'Reconnect Feed'}
        </button>
        <button style={styles.themeBtn} onClick={() => setDark(d => !d)}>
          {dark ? '☀️ Light' : '🌙 Dark'}
        </button>
        {pushSupported && (
          <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            {pushError && <span style={{ color: '#c62828', fontSize: 12 }}>{pushError}</span>}
            <button
              style={styles.btn(pushSubscribed ? '#757575' : '#1976d2', true)}
              disabled={pushLoading}
              onClick={pushSubscribed ? pushUnsubscribe : pushSubscribe}
            >
              {pushLoading ? 'Please wait…' : pushSubscribed ? 'Disable Notifications' : 'Enable Notifications'}
            </button>
          </span>
        )}
      </div>

      {/* Account selection */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Accounts</h3>
        <div style={styles.row}>
          {Object.keys(appState.accounts ?? {}).length ? (
            Object.entries(appState.accounts).map(([id, acc]) => (
              <button
                key={id}
                style={styles.accountBtn(Number(id) === currentId)}
                onClick={() => { setCurrentId(Number(id)); setSymbol(''); }}
              >
                {acc.accountName} (ID: {id})
              </button>
            ))
          ) : (
            <p style={{ color: '#999' }}>No accounts available.</p>
          )}
        </div>
      </div>

      {/* Symbol selection */}
      {currentId !== null && (
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>{symbol ? `Symbol: ${symbol}` : 'Select a Symbol'}</h3>
          <button style={styles.btn('#4caf50')} onClick={() => setSymbol('')}>Add Stock</button>
          {Object.keys(appState.accounts?.[currentId]?.stocks ?? {}).length ? (
            Object.entries(appState.accounts[currentId].stocks).map(([sym, st]) => (
              <button
                key={sym}
                style={styles.symbolBtn(st?.enabled, sym === symbol)}
                onClick={() => setSymbol(sym)}
              >
                {sym}
              </button>
            ))
          ) : (
            <p style={{ color: '#999' }}>No stocks available.</p>
          )}
        </div>
      )}

      {/* Stock detail view */}
      {currentId !== null && symbol.length > 0 && (
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>{symbol}</h3>

          <div style={styles.row}>
            <button style={styles.btn('#4caf50')} disabled={!connected}
              onClick={() => commands.setEnabled(currentId, symbol, true)}>
              Start Trading
            </button>
            <button style={styles.btn('#f44336')} disabled={!connected}
              onClick={() => commands.setEnabled(currentId, symbol, false)}>
              Stop Trading
            </button>
            <button
              style={styles.btn('#ff9800')}
              disabled={!connected || fillCooldown}
              onClick={() => {
                commands.fillMissing(currentId, symbol);
                setFillCooldown(true);
                setTimeout(() => setFillCooldown(false), 5000);
              }}
            >
              {fillCooldown ? <><span className="sono-spinner" />Please wait…</> : 'Fill Missing'}
            </button>
            <button
              style={styles.btn('#1565c0')}
              disabled={!connected || maintainCooldown}
              onClick={() => {
                commands.checkAndMaintain(currentId, symbol);
                setMaintainCooldown(true);
                setTimeout(() => setMaintainCooldown(false), 5000);
              }}
            >
              {maintainCooldown ? <><span className="sono-spinner" />Please wait…</> : 'Check & Maintain'}
            </button>
          </div>

          <div style={{ ...styles.row, marginTop: 8 }}>
            <button style={styles.btn('#e53935')} disabled={!connected}
              onClick={handleDelete}>
              Delete Stock
            </button>
            <button style={styles.btn('#e53935')} disabled={!connected}
              onClick={() => handleCopyDelete(currentId, symbol, stock)}>
              Copy and Delete Stock
            </button>
            <button style={styles.btn('#e53935')} disabled={!connected}
              onClick={() => {
                if (!window.confirm(`Cancel all orders for ${symbol}?`)) return;
                commands.cancelAllOrders(currentId, symbol);
              }}>
              Cancel All Orders
            </button>
          </div>

          <div style={{ ...styles.row, marginTop: 8 }}>
            <span style={styles.tradingBadge(stock?.enabled)}>
              {stock?.enabled ? 'TRADING ACTIVE' : 'TRADING STOPPED'}
            </span>
            <div style={styles.quotePair}>
              <span className={`quote ${flashClass(bidFlash)}`}>
                Bid: <span className="quote-value">{fmtPrice(currentBid)}</span>
              </span>
              <span className={`quote ${flashClass(askFlash)}`}>
                Ask: <span className="quote-value">{fmtPrice(currentAsk)}</span>
              </span>
            </div>
          </div>

          <div style={styles.accordion}>
            <button style={styles.accordionToggle} onClick={() => setDetailsOpen(o => !o)}>
              Details {detailsOpen ? '▲' : '▼'}
            </button>
            {detailsOpen && renderDetailsBody()}
          </div>

          {(() => {
            const raw = orders?.[currentId]?.[symbol] ?? { buy: [], sell: [] };
            const symOrders = {
              buy:  [...raw.buy].sort((a, b) => b.limitPrice - a.limitPrice),
              sell: [...raw.sell].sort((a, b) => b.limitPrice - a.limitPrice),
            };
            return (
              <div style={{ marginTop: 12 }}>
                <p style={styles.ordersHeader}>
                  Sell Orders <span style={styles.ordersBadge}>{symOrders.sell.length}</span>
                </p>
                <table style={styles.ordersTable}>
                  <thead>
                    <tr>
                      {['Order ID', 'Price', 'Qty'].map(h => (
                        <th key={h} style={styles.ordersTh}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {symOrders.sell.length === 0
                      ? <tr><td colSpan={3} style={styles.ordersEmpty}>No open sell orders</td></tr>
                      : symOrders.sell.map(o => (
                          <tr key={o.schwabOrderId}>
                            <td style={styles.ordersTd}>{o.schwabOrderId}</td>
                            <td style={styles.ordersTd}>${o.limitPrice.toFixed(2)}</td>
                            <td style={styles.ordersTd}>{o.quantity}</td>
                          </tr>
                        ))
                    }
                  </tbody>
                </table>

                <p style={{ ...styles.ordersHeader, marginTop: 10 }}>
                  Buy Orders <span style={styles.ordersBadge}>{symOrders.buy.length}</span>
                </p>
                <table style={styles.ordersTable}>
                  <thead>
                    <tr>
                      {['Order ID', 'Price', 'Qty'].map(h => (
                        <th key={h} style={styles.ordersTh}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {symOrders.buy.length === 0
                      ? <tr><td colSpan={3} style={styles.ordersEmpty}>No open buy orders</td></tr>
                      : symOrders.buy.map(o => (
                          <tr key={o.schwabOrderId}>
                            <td style={styles.ordersTd}>{o.schwabOrderId}</td>
                            <td style={styles.ordersTd}>${o.limitPrice.toFixed(2)}</td>
                            <td style={styles.ordersTd}>{o.quantity}</td>
                          </tr>
                        ))
                    }
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      )}

      {/* Add stock form */}
      {currentId !== null && symbol.length === 0 && (
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Add Stock</h3>
          <form onSubmit={handleAdd} style={styles.formGrid}>
            <label style={styles.label}>Symbol</label>
            <input style={styles.input} placeholder="Symbol" value={newStock.symbol}
              onChange={e => setField('symbol', e.target.value.toUpperCase())} required />

            <label style={styles.label}>Increment Price</label>
            <input style={styles.input} type="number" step="0.01" value={newStock.incrementPrice}
              onChange={e => setField('incrementPrice', parseFloat(e.target.value))} />

            <label style={styles.label}>Buy/Sell Range</label>
            <input style={styles.input} type="number" step="0.01" value={newStock.buySellRange}
              onChange={e => setField('buySellRange', parseFloat(e.target.value))} />

            <label style={styles.label}>Order Quantity</label>
            <input style={styles.input} type="number" step="1" value={newStock.orderQuantity}
              onChange={e => setField('orderQuantity', parseInt(e.target.value))} />

            <label style={styles.label}>Steps Above</label>
            <input style={styles.input} type="number" step="1" value={newStock.stepsAbove}
              onChange={e => setField('stepsAbove', parseInt(e.target.value))} />

            <label style={styles.label}>Steps Below</label>
            <input style={styles.input} type="number" step="1" value={newStock.stepsBelow}
              onChange={e => setField('stepsBelow', parseInt(e.target.value))} />

            <label style={styles.label}>Min Buy</label>
            <input style={styles.input} type="number" step="0.01" value={newStock.minBuy}
              onChange={e => setField('minBuy', parseFloat(e.target.value))} />

            <label style={styles.label}>Max Sell</label>
            <input style={styles.input} type="number" step="0.01" value={newStock.maxSell}
              onChange={e => setField('maxSell', parseFloat(e.target.value))} />

            <label style={styles.label}>Keep Above</label>
            <input style={styles.input} type="number" step="1" value={newStock.keepAbove}
              onChange={e => setField('keepAbove', parseInt(e.target.value))} />

            <label style={styles.label}>Trailing Buy Stop</label>
            <input style={styles.input} type="number" step="0.01" value={newStock.trailingBuyStop}
              onChange={e => setField('trailingBuyStop', parseFloat(e.target.value))} />

            <label style={styles.label}>Trailing Sell Stop</label>
            <input style={styles.input} type="number" step="0.01" value={newStock.trailingSellStop}
              onChange={e => setField('trailingSellStop', parseFloat(e.target.value))} />

            <label style={styles.label}>Offset</label>
            <input style={styles.input} type="number" step="0.01" value={newStock.offset}
              onChange={e => setField('offset', parseFloat(e.target.value))} />

            <button style={{ ...styles.btn('#1976d2'), gridColumn: '1 / -1' }} type="submit" disabled={!connected}>
              Add Stock
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────
const makeStyles = (dark) => {
  const c = dark ? {
    text:        '#e0e0e0',
    subText:     '#9e9e9e',
    labelText:   '#bdbdbd',
    cardBg:      '#1e1e1e',
    border:      '#383838',
    barBg:       '#1e1e1e',
    inputBg:     '#2a2a2a',
    inputBorder: '#4a4a4a',
    neutralBg:   '#2a2a2a',
    neutralText: '#ddd',
    headBg:      '#2a2a2a',
    rowBorder:   '#2f2f2f',
    badgeBg:     '#3a3a3a',
    badgeText:   '#bbb',
  } : {
    text:        '#222',
    subText:     '#555',
    labelText:   '#444',
    cardBg:      '#fff',
    border:      '#e0e0e0',
    barBg:       '#f5f5f5',
    inputBg:     '#fff',
    inputBorder: '#ccc',
    neutralBg:   '#f0f0f0',
    neutralText: '#333',
    headBg:      '#f5f5f5',
    rowBorder:   '#f0f0f0',
    badgeBg:     '#e0e0e0',
    badgeText:   '#555',
  };

  return {
    page:      { maxWidth: 760, margin: '2rem auto', fontFamily: 'sans-serif', padding: '0 1rem', color: c.text },
    statusBar: {
      display: 'flex', alignItems: 'center', gap: 16,
      padding: '8px 14px', background: c.barBg,
      borderRadius: 6, marginBottom: 16, fontSize: 13,
    },
    dot:          { width: 9, height: 9, borderRadius: '50%', display: 'inline-block', margin: '0 5px' },
    tradingBadge: (active) => ({
      padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700,
      background: active ? (dark ? '#1b3a1e' : '#e8f5e9') : (dark ? '#3e2126' : '#fce4ec'),
      color:      active ? (dark ? '#81c784' : '#2e7d32') : (dark ? '#ef9a9a' : '#c62828'),
    }),
    card:      { border: `1px solid ${c.border}`, borderRadius: 8, padding: 16, marginBottom: 16, background: c.cardBg },
    cardTitle: { margin: '0 0 12px', fontSize: 15, fontWeight: 600 },
    row:       { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
    // Bid and Ask are one unit. They live inside the wrapping row above, so on a
    // narrow phone the row is free to push them below the TRADING badge — but
    // nowrap here keeps them on the SAME line as each other rather than letting
    // the pair split, which reads as two unrelated values.
    quotePair: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'nowrap' },
    input:     { padding: '7px 10px', borderRadius: 4, border: `1px solid ${c.inputBorder}`, fontSize: 14, background: c.inputBg, color: c.text },
    formGrid:  { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', alignItems: 'center' },
    label:     { fontSize: 13, fontWeight: 600, color: c.labelText },
    symbolBtn: (enabled, active) => ({
      padding: '7px 16px',
      background: active ? '#1976d2' : c.neutralBg,
      color:      active ? '#fff'    : c.neutralText,
      border:     enabled ? '2px solid #4caf50' : `1px solid ${c.inputBorder}`,
      borderRadius: 4, cursor: 'pointer', fontSize: 14, fontWeight: active ? 600 : 400,
    }),
    accountBtn: (active) => ({
      padding: '7px 16px',
      background: active ? '#1976d2' : c.neutralBg,
      color:      active ? '#fff'    : c.neutralText,
      border:     active ? 'none'    : `1px solid ${c.inputBorder}`,
      borderRadius: 4, cursor: 'pointer', fontSize: 14, fontWeight: active ? 600 : 400,
    }),
    btn: (color, small = false) => ({
      padding: small ? '5px 12px' : '7px 16px',
      background: color, color: '#fff', border: 'none',
      borderRadius: 4, cursor: 'pointer', fontSize: small ? 12 : 14,
    }),
    themeBtn: {
      padding: '5px 12px', background: 'transparent', color: 'inherit',
      border: `1px solid ${c.inputBorder}`, borderRadius: 4, cursor: 'pointer', fontSize: 12,
    },
    accordion:       { marginTop: 12, border: `1px solid ${c.border}`, borderRadius: 6 },
    accordionToggle: {
      width: '100%', textAlign: 'left', padding: '8px 12px',
      background: c.headBg, border: 'none', borderRadius: 6,
      fontSize: 14, fontWeight: 600, cursor: 'pointer', color: c.text,
    },
    accordionBody: { padding: '10px 12px' },
    detailTable:   { width: '100%', borderCollapse: 'collapse' },
    detailLabel:   { fontSize: 13, fontWeight: 600, color: c.subText, padding: '4px 8px 4px 0', width: '50%' },
    detailValue:   { fontSize: 13, color: c.text, padding: '4px 0' },
    ordersHeader:  { margin: '0 0 4px', fontSize: 13, fontWeight: 600, color: c.labelText, display: 'flex', alignItems: 'center', gap: 6 },
    ordersBadge:   { background: c.badgeBg, borderRadius: 10, padding: '1px 7px', fontSize: 12, fontWeight: 700, color: c.badgeText },
    ordersTable:   { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    ordersTh:      { textAlign: 'left', padding: '4px 8px', background: c.headBg, fontWeight: 600, color: c.subText, borderBottom: `1px solid ${c.border}` },
    ordersTd:      { padding: '4px 8px', borderBottom: `1px solid ${c.rowBorder}`, color: c.text },
    ordersEmpty:   { padding: '6px 8px', color: '#aaa', fontStyle: 'italic' },
  };
};
